import Phaser from "phaser";
import type { StageConfig } from "../config/stages.ts";
import { bakeTexture } from "../art/bake.ts";
import { css, hash01, mix, shade } from "../art/color.ts";

interface Layer {
  sprite: Phaser.GameObjects.TileSprite;
  /** Fraction of camera movement applied to the tile offset (0 = fixed, 1 = world speed). */
  factor: number;
  /** Extra horizontal drift in px/s (clouds). */
  drift: number;
  /** Top edge as a fraction of screen height, or (when `onHorizon`) the px height of the
   * hill silhouette above the terrain horizon line. */
  screenY: number;
  onHorizon: boolean;
}

/** Solid fill baked under each hill silhouette so terrain valleys never reveal sky below it. */
const HILL_SKIRT = 700;
/** How far below the averaged terrain line the hill silhouettes' base sits. */
const HORIZON_SINK = 40;

const HILL_TILE_WIDTH = 1024;
const HILL_LAYERS = [
  { height: 300, factor: 0.08, haze: 0.55, amps: [34, 18, 10, 5] },
  { height: 230, factor: 0.18, haze: 0.32, amps: [28, 16, 9, 4] },
  { height: 160, factor: 0.32, haze: 0.12, amps: [22, 12, 7, 3] },
];

function isDarkSky(stage: StageConfig): boolean {
  const c = stage.skyColorTop;
  const luminance = (0.299 * ((c >> 16) & 0xff) + 0.587 * ((c >> 8) & 0xff) + 0.114 * (c & 0xff)) / 255;
  return luminance < 0.3;
}

/**
 * Sky gradient, clouds (or stars on dark stages) and layered hazy hills behind the terrain.
 * Everything is screen-anchored and compensated for camera zoom each frame, so the backdrop
 * keeps filling the screen while the camera zooms out at speed. Scrolling is faked by
 * shifting each TileSprite's texture offset by a fraction of the camera's movement.
 */
export class ParallaxBackground {
  private readonly sky: Phaser.GameObjects.Image;
  private readonly layers: Layer[] = [];
  private horizon: number | null = null;
  private elapsed = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    stage: StageConfig,
    camera: Phaser.Cameras.Scene2D.Camera,
    /** World-space Y of the ground surface at a world X (used to seat the hills). */
    private readonly groundYAt?: (worldX: number) => number,
  ) {
    const skyKey = bakeTexture(
      scene,
      `sky-${stage.id}`,
      4,
      256,
      (ctx) => {
        const grad = ctx.createLinearGradient(0, 0, 0, 256);
        grad.addColorStop(0, css(stage.skyColorTop));
        grad.addColorStop(1, css(stage.skyColorBottom));
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, 4, 256);
      },
      1,
    );
    this.sky = scene.add.image(0, 0, skyKey).setOrigin(0, 0).setScrollFactor(0).setDepth(-30);

    if (isDarkSky(stage)) {
      this.addLayer(this.bakeStars(stage), 0.02, 0, 0, false, -29, 1);
    } else {
      this.addLayer(this.bakeClouds(stage), 0.04, 6, 0.04, false, -29, 0.95);
    }

    HILL_LAYERS.forEach((spec, i) => {
      const key = this.bakeHills(stage, i);
      this.addLayer(key, spec.factor, 0, spec.height, true, -20 + i, 1);
    });

    this.update(camera, 0);
  }

  private addLayer(
    key: string,
    factor: number,
    drift: number,
    screenY: number,
    onHorizon: boolean,
    depth: number,
    alpha: number,
  ): void {
    const frame = this.scene.textures.getFrame(key);
    const sprite = this.scene.add
      .tileSprite(0, 0, 256, frame.height, key)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(depth)
      .setAlpha(alpha);
    this.layers.push({ sprite, factor, drift, screenY, onHorizon });
  }

  private bakeClouds(stage: StageConfig): string {
    const w = 1400;
    const h = 280;
    return bakeTexture(
      this.scene,
      `clouds-${stage.id}`,
      w,
      h,
      (ctx) => {
        const clouds = [
          { x: 160, y: 120, s: 1 },
          { x: 560, y: 170, s: 0.7 },
          { x: 900, y: 110, s: 1.2 },
          { x: 1240, y: 160, s: 0.8 },
        ];
        const tint = mix(0xffffff, stage.skyColorTop, 0.12);
        for (const c of clouds) {
          const puffs = [
            [-60, 10, 34],
            [-25, -12, 44],
            [20, -20, 50],
            [62, 0, 38],
            [5, 14, 40],
          ];
          // Soft underside shadow, then the bright puffs.
          ctx.fillStyle = css(shade(tint, -0.12), 0.9);
          for (const [px, py, r] of puffs) {
            ctx.beginPath();
            ctx.arc(c.x + px! * c.s, c.y + (py! + 6) * c.s, r! * c.s, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.fillStyle = css(0xffffff);
          for (const [px, py, r] of puffs) {
            ctx.beginPath();
            ctx.arc(c.x + px! * c.s, c.y + py! * c.s, r! * c.s, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      },
      1,
    );
  }

  private bakeStars(stage: StageConfig): string {
    const w = 1024;
    const h = 420;
    return bakeTexture(
      this.scene,
      `stars-${stage.id}`,
      w,
      h,
      (ctx) => {
        for (let i = 0; i < 140; i++) {
          const x = hash01(i, 11) * w;
          const y = hash01(i, 12) * h;
          const r = 0.6 + hash01(i, 13) * 1.4;
          ctx.fillStyle = css(0xffffff, 0.35 + hash01(i, 14) * 0.6);
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fill();
        }
      },
      1,
    );
  }

  private bakeHills(stage: StageConfig, index: number): string {
    const spec = HILL_LAYERS[index]!;
    const base = stage.parallaxColors[index] ?? stage.parallaxColors[0] ?? 0x7fc48f;
    const color = mix(base, stage.skyColorBottom, spec.haze);
    const w = HILL_TILE_WIDTH;
    const h = spec.height + HILL_SKIRT;
    return bakeTexture(
      this.scene,
      `hills-${stage.id}-${index}`,
      w,
      h,
      (ctx) => {
        // Sum of sines with whole-number periods across the tile, so it wraps seamlessly.
        const phases = [0.3, 1.7, 2.9, 4.1].map((p) => p + index * 1.3);
        const freqs = [1, 2, 3, 5];
        const crest = spec.amps.reduce((a, b) => a + b, 0);
        const yAt = (x: number) => {
          let y = crest + 6;
          freqs.forEach((f, k) => {
            y -= spec.amps[k]! * Math.sin((2 * Math.PI * f * x) / w + phases[k]!);
          });
          return y;
        };
        const outline = () => {
          ctx.beginPath();
          for (let x = 0; x <= w; x += 8) {
            if (x === 0) ctx.moveTo(x, yAt(x));
            else ctx.lineTo(x, yAt(x));
          }
        };

        outline();
        ctx.lineTo(w, h);
        ctx.lineTo(0, h);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, 0, 0, h);
        grad.addColorStop(0, css(shade(color, 0.12)));
        grad.addColorStop(spec.height / h, css(shade(color, -0.1)));
        grad.addColorStop(1, css(shade(color, -0.2)));
        ctx.fillStyle = grad;
        ctx.fill();

        ctx.strokeStyle = css(shade(color, 0.3), 0.8);
        ctx.lineWidth = 3;
        outline();
        ctx.stroke();
        // Keep the bottom rows empty: the tile sprite's wrap sampling would otherwise bleed
        // them into the transparent top edge as a faint line across the sky.
        ctx.clearRect(0, h - 2, w, 2);
      },
      1,
    );
  }

  update(camera: Phaser.Cameras.Scene2D.Camera, deltaSeconds = 1 / 60): void {
    this.elapsed += deltaSeconds;
    const zoom = camera.zoom || 1;
    const W = camera.width;
    const H = camera.height;
    // Scroll-factor-0 objects still get the camera zoom (about the screen centre), so place
    // and scale them in "pre-zoom" space to land on the intended screen rectangle.
    const toWorldX = (sx: number) => W / 2 + (sx - W / 2) / zoom;
    const toWorldY = (sy: number) => H / 2 + (sy - H / 2) / zoom;

    this.sky.setPosition(toWorldX(0), toWorldY(0)).setDisplaySize(W / zoom, H / zoom);

    // Seat the hills on the average terrain line across the view, smoothed so bumps under the
    // car don't make the whole backdrop bounce. Falls back to the lower part of the screen.
    let target = H * 0.7;
    if (this.groundYAt) {
      const view = camera.worldView;
      let sum = 0;
      const samples = 9;
      for (let i = 0; i < samples; i++) sum += this.groundYAt(view.x + (view.width * i) / (samples - 1));
      const worldY = sum / samples;
      target = (worldY - (camera.scrollY + H / 2)) * zoom + H / 2;
    }
    if (this.horizon === null) this.horizon = target;
    else this.horizon += (target - this.horizon) * Math.min(1, deltaSeconds * 3);

    for (const layer of this.layers) {
      const sprite = layer.sprite;
      if (sprite.width !== W) sprite.setSize(W, sprite.height);
      const screenTop = layer.onHorizon
        ? this.horizon + HORIZON_SINK - layer.screenY
        : H * layer.screenY;
      sprite.setPosition(toWorldX(0), toWorldY(screenTop)).setScale(1 / zoom);
      sprite.tilePositionX = camera.scrollX * layer.factor + this.elapsed * layer.drift;
    }
  }

  destroy(): void {
    this.sky.destroy();
    for (const layer of this.layers) layer.sprite.destroy();
    this.layers.length = 0;
  }
}
